import { Component, EventEmitter, Input, Output } from '@angular/core';

@Component({
  selector: 'app-date-picker-custom',
  templateUrl: './date-picker-custom.component.html',
  styleUrls: ['./date-picker-custom.component.scss']
})
export class DatePickerCustomComponent {
@Input() placeholder: string = 'Select date range';
  @Input() isRange: boolean = true;
  @Input() startDate: Date | null = null;
  @Input() endDate: Date | null = null;
  @Output() dateChange = new EventEmitter<{ startDate: Date | null, endDate: Date | null }>();
  @Output() startDateChange = new EventEmitter<Date | null>();
  @Output() endDateChange = new EventEmitter<Date | null>();
  tempStartDate: Date | null = null;
  tempEndDate: Date | null = null;

  showCalendar = false;
  calendarView: 'year' | 'month' | 'date' = 'date';
  selectedYear: number = new Date().getFullYear();
  selectedMonth: number = new Date().getMonth();
  currentDate = new Date();
  calendarDays: (Date | null)[] = [];
  years: number[] = [];
  displayedYears: number[] = [];
  currentYearPage = 0;
  yearsPerPage = 12;

  monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  private onChange: any = () => {};
  private onTouched: any = () => {};

  ngOnInit() {
    this.generateYears();
    this.generateCalendarDays();
  }

  writeValue(value: any): void {
    if (value) {
      this.startDate = value.startDate;
      this.endDate = value.endDate;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  toggleCalendar() {
    this.showCalendar = !this.showCalendar;
    if (this.showCalendar) {
      this.calendarView = 'date';
      this.tempStartDate = this.startDate;
      this.tempEndDate = this.endDate;
    }
  }

  clearDate() {
    this.startDate = null;
    this.endDate = null;
    this.tempStartDate = null;
    this.tempEndDate = null;
    this.onChange({ startDate: null, endDate: null });
    this.dateChange.emit({ startDate: null, endDate: null });
    this.startDateChange.emit(null);
    this.endDateChange.emit(null);
  }

  selectYear(year: number) {
    this.selectedYear = year;
    this.calendarView = 'month';
  }

  selectMonth(monthIndex: number) {
    this.selectedMonth = monthIndex;
    this.currentDate = new Date(this.selectedYear, monthIndex, 1);
    this.calendarView = 'date';
    this.generateCalendarDays();
  }

  selectDate(date: Date | null) {
    if (!date) return;
    
    if (!this.isRange) {
      this.tempStartDate = date;
      this.tempEndDate = null;
    } else {
      if (!this.tempStartDate || this.tempEndDate) {
        this.tempStartDate = date;
        this.tempEndDate = null;
      } else if (date >= this.tempStartDate) {
        this.tempEndDate = date;
      } else {
        this.tempStartDate = date;
        this.tempEndDate = null;
      }
    }
  }

  backToYearView() {
    this.calendarView = 'year';
  }

  backToMonthView() {
    this.calendarView = 'month';
  }

  confirmSelection() {
    if (this.tempStartDate) {
      this.startDate = this.tempStartDate;
      this.endDate = this.tempEndDate;
      const value = { startDate: this.startDate, endDate: this.endDate };
      this.onChange(value);
      this.dateChange.emit(value);
      this.startDateChange.emit(this.startDate);
      this.endDateChange.emit(this.endDate);
    }
    this.showCalendar = false;
  }

  cancelSelection() {
    this.tempStartDate = null;
    this.tempEndDate = null;
    this.showCalendar = false;
  }

  isDateInRange(date: Date | null): boolean {
    if (!date || !this.tempStartDate) return false;
    if (!this.tempEndDate) return this.isSameDate(date, this.tempStartDate);
    return date >= this.tempStartDate && date <= this.tempEndDate;
  }

  isStartDate(date: Date | null): boolean {
    return date && this.tempStartDate ? this.isSameDate(date, this.tempStartDate) : false;
  }

  isEndDate(date: Date | null): boolean {
    return date && this.tempEndDate ? this.isSameDate(date, this.tempEndDate) : false;
  }

  isCurrentMonth(date: Date | null): boolean {
    if (!date) return true;
    return date.getMonth() === this.currentDate.getMonth();
  }

  generateCalendarDays() {
    const year = this.currentDate.getFullYear();
    const month = this.currentDate.getMonth();
    const firstDayIndex = new Date(year, month, 1).getDay();
    const lastDayDate = new Date(year, month + 1, 0).getDate();
    const prevLastDayDate = new Date(year, month, 0).getDate();

    this.calendarDays = [
      ...Array.from({ length: firstDayIndex }, (_, i) => new Date(year, month - 1, prevLastDayDate - firstDayIndex + i + 1)),
      ...Array.from({ length: lastDayDate }, (_, i) => new Date(year, month, i + 1)),
      ...Array.from({ length: 42 - firstDayIndex - lastDayDate }, (_, i) => new Date(year, month + 1, i + 1))
    ];
  }

  generateYears() {
    const currentYear = new Date().getFullYear();
    this.years = [];
    for (let i = currentYear - 25; i <= currentYear + 25; i++) {
      this.years.push(i);
    }
    const currentYearIndex = this.years.indexOf(currentYear);
    this.currentYearPage = Math.floor(currentYearIndex / this.yearsPerPage);
    this.updateDisplayedYears();
  }

  prevMonth() {
    this.currentDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() - 1, 1);
    this.selectedYear = this.currentDate.getFullYear();
    this.selectedMonth = this.currentDate.getMonth();
    this.generateCalendarDays();
  }

  nextMonth() {
    this.currentDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 1);
    this.selectedYear = this.currentDate.getFullYear();
    this.selectedMonth = this.currentDate.getMonth();
    this.generateCalendarDays();
  }

  getYearPageRange(): string {
    return this.displayedYears.length ? `${this.displayedYears[0]} - ${this.displayedYears[this.displayedYears.length - 1]}` : '';
  }

  prevYearPage() {
    if (this.currentYearPage > 0) this.currentYearPage--;
    this.updateDisplayedYears();
  }

  nextYearPage() {
    this.currentYearPage++;
    const requiredYears = (this.currentYearPage + 1) * this.yearsPerPage;
    if (requiredYears > this.years.length) {
      const lastYear = this.years[this.years.length - 1];
      this.years.push(...Array.from({ length: requiredYears - this.years.length }, (_, i) => lastYear + i + 1));
    }
    this.updateDisplayedYears();
  }

  updateDisplayedYears() {
    const startIndex = this.currentYearPage * this.yearsPerPage;
    this.displayedYears = this.years.slice(startIndex, startIndex + this.yearsPerPage);
  }

  getDateLabel(): string {
    if (!this.startDate) return '';
    const format = { month: 'short', day: 'numeric', year: 'numeric' } as const;
    const start = this.startDate.toLocaleDateString('en-US', format);
    if (!this.endDate || !this.isRange) return start;
    const end = this.endDate.toLocaleDateString('en-US', format);
    return `${start} - ${end}`;
  }

  isSameDate(date1: Date, date2: Date): boolean {
    return date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear();
  }
}
